import { useEffect, useState } from "react";
import axios from "axios";
import { CheckCircle2, Mail, Phone, Building2, User, Loader2 } from "lucide-react";
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogDescription,
} from "@/components/ui/dialog";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const validEmail = (v) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v);
// Optional, but if provided must look like a phone number
const validPhone = (v) => !v || /^[+\d][\d\s()\-]{6,24}$/.test(v.trim());

export default function ContactModal({ open, onOpenChange }) {
    const [company, setCompany] = useState("");
    const [contactPerson, setContactPerson] = useState("");
    const [email, setEmail] = useState("");
    const [phone, setPhone] = useState("");
    const [submitting, setSubmitting] = useState(false);
    const [done, setDone] = useState(false);
    const [error, setError] = useState("");

    useEffect(() => {
        if (open) {
            setCompany("");
            setContactPerson("");
            setEmail("");
            setPhone("");
            setError("");
            setDone(false);
            setSubmitting(false);
        }
    }, [open]);

    const submit = async (e) => {
        e?.preventDefault?.();
        setError("");
        if (!company.trim()) return setError("Please enter your company name");
        if (!contactPerson.trim()) return setError("Please enter the contact person's name");
        if (!validEmail(email)) return setError("Please enter a valid email");
        if (!validPhone(phone))
            return setError("Please enter a valid phone number or leave it blank");

        setSubmitting(true);
        try {
            await axios.post(`${API}/leads`, {
                name: contactPerson.trim(),
                email: email.trim(),
                company: company.trim(),
                phone: phone.trim() || null,
                intent: "contact_request",
                source: "landing_page",
            });
            setDone(true);
        } catch (err) {
            setError(
                err?.response?.data?.detail
                    ? "We couldn't save your details. Please try again."
                    : "Network error. Please try again."
            );
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent
                data-testid="contact-modal"
                className="sm:max-w-md p-0 overflow-hidden border-emerald-500/20 bg-white"
            >
                <div className="relative">
                    <div
                        aria-hidden
                        className="absolute inset-0 bg-gradient-to-br from-[#04110D] via-[#063a2c] to-[#04110D]"
                    />
                    <div className="relative px-6 pt-7 pb-7">
                        <span className="inline-flex items-center gap-2 rounded-full border border-emerald-400/30 bg-emerald-500/10 px-2.5 py-1 text-[10px] tracking-[0.25em] uppercase font-bold text-emerald-200">
                            Contact us
                        </span>
                        <DialogHeader className="mt-3 text-left">
                            <DialogTitle className="font-heading text-2xl font-extrabold text-white">
                                Get in touch with TruoPay
                            </DialogTitle>
                            <DialogDescription className="text-zinc-300 mt-1">
                                Share a few details and our team will reach out shortly.
                            </DialogDescription>
                        </DialogHeader>
                    </div>
                </div>

                <div className="px-6 py-6">
                    {done ? (
                        <div data-testid="contact-modal-success" className="text-center py-6">
                            <div className="mx-auto h-12 w-12 rounded-full bg-emerald-100 flex items-center justify-center">
                                <CheckCircle2 className="h-6 w-6 text-emerald-700" />
                            </div>
                            <h3 className="mt-4 font-heading text-xl font-bold text-zinc-950">
                                Thank you!
                            </h3>
                            <p className="mt-2 text-sm text-zinc-600">
                                Our team will contact you soon.
                            </p>
                            <button
                                type="button"
                                data-testid="contact-modal-close-btn"
                                onClick={() => onOpenChange(false)}
                                className="mt-6 inline-flex h-11 items-center justify-center rounded-full bg-zinc-950 px-6 text-sm font-bold text-white hover:bg-emerald-700 transition-colors"
                            >
                                Done
                            </button>
                        </div>
                    ) : (
                        <form onSubmit={submit} noValidate className="space-y-4">
                            <label className="block">
                                <span className="text-xs font-bold tracking-widest uppercase text-zinc-500">
                                    Company
                                </span>
                                <div className="mt-1.5 relative">
                                    <Building2 className="absolute left-3.5 top-1/2 -translate-y-1/2 h-4 w-4 text-zinc-400" />
                                    <input
                                        type="text"
                                        autoFocus
                                        required
                                        data-testid="contact-modal-company-input"
                                        value={company}
                                        onChange={(e) => setCompany(e.target.value)}
                                        placeholder="Acme Exports Pvt Ltd"
                                        className="h-12 w-full rounded-xl border border-zinc-200 pl-10 pr-4 text-base text-zinc-950 focus:border-emerald-500 focus:outline-none transition-colors"
                                    />
                                </div>
                            </label>

                            <label className="block">
                                <span className="text-xs font-bold tracking-widest uppercase text-zinc-500">
                                    Contact Person
                                </span>
                                <div className="mt-1.5 relative">
                                    <User className="absolute left-3.5 top-1/2 -translate-y-1/2 h-4 w-4 text-zinc-400" />
                                    <input
                                        type="text"
                                        required
                                        data-testid="contact-modal-name-input"
                                        value={contactPerson}
                                        onChange={(e) => setContactPerson(e.target.value)}
                                        placeholder="Ananya Mehta"
                                        className="h-12 w-full rounded-xl border border-zinc-200 pl-10 pr-4 text-base text-zinc-950 focus:border-emerald-500 focus:outline-none transition-colors"
                                    />
                                </div>
                            </label>

                            <label className="block">
                                <span className="text-xs font-bold tracking-widest uppercase text-zinc-500">
                                    Email ID
                                </span>
                                <div className="mt-1.5 relative">
                                    <Mail className="absolute left-3.5 top-1/2 -translate-y-1/2 h-4 w-4 text-zinc-400" />
                                    <input
                                        type="email"
                                        required
                                        data-testid="contact-modal-email-input"
                                        value={email}
                                        onChange={(e) => setEmail(e.target.value)}
                                        placeholder="ananya@company.com"
                                        className="h-12 w-full rounded-xl border border-zinc-200 pl-10 pr-4 text-base text-zinc-950 focus:border-emerald-500 focus:outline-none transition-colors"
                                    />
                                </div>
                            </label>

                            <label className="block">
                                <span className="text-xs font-bold tracking-widest uppercase text-zinc-500">
                                    Phone Number <span className="font-normal lowercase text-zinc-400 tracking-normal">(optional)</span>
                                </span>
                                <div className="mt-1.5 relative">
                                    <Phone className="absolute left-3.5 top-1/2 -translate-y-1/2 h-4 w-4 text-zinc-400" />
                                    <input
                                        type="tel"
                                        data-testid="contact-modal-phone-input"
                                        value={phone}
                                        onChange={(e) => setPhone(e.target.value)}
                                        placeholder="+91 98xxxxxxxx"
                                        className="h-12 w-full rounded-xl border border-zinc-200 pl-10 pr-4 text-base text-zinc-950 focus:border-emerald-500 focus:outline-none transition-colors"
                                    />
                                </div>
                            </label>

                            {error && (
                                <div
                                    data-testid="contact-modal-error"
                                    className="text-sm text-red-600"
                                >
                                    {error}
                                </div>
                            )}

                            <button
                                type="submit"
                                disabled={submitting}
                                data-testid="contact-modal-submit-btn"
                                className="mt-2 inline-flex h-12 w-full items-center justify-center gap-2 rounded-full bg-zinc-950 px-6 text-base font-bold text-white hover:bg-emerald-700 transition-colors disabled:opacity-60"
                            >
                                {submitting ? (
                                    <>
                                        <Loader2 className="h-4 w-4 animate-spin" />
                                        Submitting…
                                    </>
                                ) : (
                                    <>Submit</>
                                )}
                            </button>
                            <p className="text-[11px] text-zinc-500 text-center">
                                By submitting you agree to be contacted by the TruoPay team. No spam.
                            </p>
                        </form>
                    )}
                </div>
            </DialogContent>
        </Dialog>
    );
}
